import { IsEnum, IsNotEmpty, IsString } from 'class-validator';

//Did OpenAI migration following this guide https://github.com/openai/openai-node/discussions/217
import OpenAI from 'openai';

export enum ChatRole {
	System = 'system',
	User = 'user',
	Assistant = 'assistant'
};

export class GPTMessage {
	@IsEnum(ChatRole)
	role: ChatRole;

	@IsString()
	@IsNotEmpty()
	content: string;
}

export class ChatGPT {
	private static readonly apiKey: string = process.env.OPEN_AI_API_KEY;
	private static readonly model: string = process.env.OPEN_AI_MODEL || 'gpt-4o';
	private static readonly maxTokens: number = (process.env.OPEN_AI_CHAT_GPT_MAX_TOKENS) ? Number(process.env.OPEN_AI_CHAT_GPT_MAX_TOKENS) : 2000;
	private static get isConfigured() {
		return !!this.apiKey;
	}

	private static get client() {
		return new OpenAI({ apiKey: this.apiKey });
	}

	public static async query(
		messages: GPTMessage[],
		temperature: number = 1,
		model: string = this.model,
	) {
		if(!this.isConfigured) {
			throw new Error('OpenAI API key is not configured.');
		}


		console.log('OpenAI Model', model || this.model);

		return this.client
			.chat.completions.create({
				model: model,
				temperature: temperature,
				messages: messages
			});
	}

	/**
	 * Query OpenAI with JSON response format
	 * @param messages - Array of GPT messages
	 * @param temperature - Temperature for the response
	 * @param model - Model to use
	 * @returns OpenAI response with JSON format
	 */
	public static async queryWithJsonResponse(
		messages: GPTMessage[],
		temperature: number = 1,
		model: string = this.model,
	) {
		if(!this.isConfigured) {
			throw new Error('OpenAI API key is not configured.');
		}


		console.log('OpenAI Model', model || this.model);

		return this.client.chat.completions.create({
			model: model,
			temperature: temperature,
			messages: messages,
			response_format: { type: 'json_object' }
		});
	}

	/**
	 * Generate images using OpenAI images API
	 * @param prompt - Text prompt for image generation
	 * @param options - Generation options including model, size, quality, and reference images
	 * @returns Array of generated images with URLs and base64 data
	 */
	public static async generateImages(
		prompt: string,
		options?: {
			model?: string;
			n?: number;
			size?: '256x256' | '512x512' | '1024x1024' | '1536x1024' | '1024x1536' | 'auto';
			quality?: 'standard' | 'hd' | 'low' | 'medium' | 'high' | 'auto';
			referenceImages?: string[]; // Base64-encoded images or URLs
		},
	) {
		if (!this.isConfigured) {
			throw new Error('OpenAI API key is not configured.');
		}
		const model = options?.model || 'gpt-image-1';
		const n = options?.n ?? 4;
		const size = (options?.size || '1024x1024') as any; // allow extended sizes not yet in older SDK types
		const quality = (options?.quality || 'auto') as any; // cast to satisfy SDK types that expect 'standard'|'hd' while API accepts 'auto'|'low'|'medium'|'high'

		// Build request payload
		const requestPayload: any = {
			model,
			prompt,
			n,
			size,
			quality: quality as any,
		};

		// TODO: OpenAI API does not yet support reference images for image generation
		// This parameter is prepared for future API support or alternative endpoints
		// When OpenAI adds support, reference images will be passed here
		if (options?.referenceImages && options.referenceImages.length > 0) {
			// Reference images would be added to the request when API supports it
			// Possible future format: requestPayload.reference_images = options.referenceImages;
			console.warn(
				'Reference images provided but not yet supported by OpenAI image generation API',
			);
		}

		const res = await this.client.images.generate(requestPayload);
		return res.data?.map((d) => ({ url: d.url, b64_json: d.b64_json }));
	}
}
